import type { Ref } from "vue";

export type CheckoutProcessingStep =
  | "creating"
  | "paying"
  | "confirming"
  | "success"
  | "error";

/**
 * useCheckoutProcessingProgress — état du modal de progression paiement
 * (création → paiement → confirmation → success) partagé par les pages
 * /a/[slug] (adhésion) et /b/[slug] (billetterie). Extrait 1:1 (le code
 * était dupliqué à l'identique dans les deux pages — Zone A split).
 *
 * Les composables `useMembershipOrderSubmit` / `useEventOrderSubmit`
 * mettent à jour `processingStep`/`processingProgress` au fil des étapes et
 * `<AslugProcessingOverlay>` / `<BslugProcessingOverlay>` s'affichent dès
 * `isProcessing`.
 */
export function useCheckoutProcessingProgress(): {
  processingStep: Ref<CheckoutProcessingStep>;
  processingProgress: Ref<number>;
  startProgress: (target: number, duration?: number) => void;
  stopProgress: () => void;
} {
  const processingStep = ref<CheckoutProcessingStep>("creating");
  const processingProgress = ref(0);
  let progressInterval: ReturnType<typeof setInterval> | null = null;

  function startProgress(target: number, duration: number = 2000) {
    if (progressInterval) clearInterval(progressInterval);
    const step = (target - processingProgress.value) / (duration / 50);
    progressInterval = setInterval(() => {
      processingProgress.value = Math.min(processingProgress.value + step, target);
      if (processingProgress.value >= target) {
        if (progressInterval) clearInterval(progressInterval);
      }
    }, 50);
  }

  function stopProgress() {
    if (progressInterval) {
      clearInterval(progressInterval);
      progressInterval = null;
    }
  }

  return { processingStep, processingProgress, startProgress, stopProgress };
}
